/**
 * routeUtils — ScheduleDraft 航路引用数据与匹配语义工具层。
 *
 * 本模块集中处理航路列表/映射读取、航段匹配、机型兼容与引用数据合并。
 * 底层航路字段读取（如 BH、展示名称）仍保留在 flightDataUtils。
 */

import { normalizeAirportCode } from "../../../../utils/airportCodeUtils";

export { normalizeAirportCode };

export function normalizeAircraftIdentifier(value) {
    return String(value ?? "").trim().toUpperCase();
}

export function valuesFromMapOrObject(value) {
    if (!value) {
        return [];
    }
    if (value instanceof Map) {
        return Array.from(value.values());
    }
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === "object") {
        return Object.values(value);
    }

    return [];
}

export function routesByIdToObject(routesById) {
    if (routesById instanceof Map) {
        return Object.fromEntries(routesById.entries());
    }

    return routesById || {};
}

export function getRoutesById(referenceData = {}) {
    return routesByIdToObject(referenceData?.routesById);
}

export function normalizeRouteList(routes) {
    return (Array.isArray(routes) ? routes : valuesFromMapOrObject(routes))
        .map((route) => route?.route || route)
        .filter(Boolean);
}

export function getRoutesByLeg(referenceData = {}, departureAirport, arrivalAirport) {
    const routesByLeg = referenceData?.routesByLeg;
    if (!routesByLeg || !departureAirport || !arrivalAirport) {
        return null;
    }

    const routeKeys = [
        `${departureAirport}:${arrivalAirport}`,
        `${departureAirport}-${arrivalAirport}`,
        `${departureAirport}_${arrivalAirport}`,
    ];

    if (routesByLeg instanceof Map) {
        return routeKeys.reduce((found, key) => found || routesByLeg.get(key), null);
    }

    return routeKeys.reduce((found, key) => found || routesByLeg[key], null);
}

function routesByLegToObject(routesByLeg) {
    if (routesByLeg instanceof Map) {
        return Object.fromEntries(routesByLeg.entries());
    }

    if (routesByLeg && typeof routesByLeg === "object" && !Array.isArray(routesByLeg)) {
        return routesByLeg;
    }

    return {};
}

export function getReferenceRoutes(referenceData = {}, departureAirport, arrivalAirport) {
    const routesByLeg = getRoutesByLeg(referenceData, departureAirport, arrivalAirport);
    if (routesByLeg) {
        return normalizeRouteList(routesByLeg);
    }

    if (Array.isArray(referenceData?.routes)) {
        return normalizeRouteList(referenceData.routes);
    }

    return normalizeRouteList(Object.values(getRoutesById(referenceData)));
}

export function getRouteById(referenceData = {}, routeId) {
    if (routeId === undefined || routeId === null || routeId === "") {
        return null;
    }

    const routesById = getRoutesById(referenceData);
    return routesById?.[routeId] || routesById?.[String(routeId)] || null;
}

export function routeMatchesLeg(route, departureAirport, arrivalAirport) {
    return normalizeAirportCode(route?.departure_airport) === departureAirport
        && normalizeAirportCode(route?.arrival_airport) === arrivalAirport;
}

export function parseCompatibleAircraft(value) {
    if (value === undefined || value === null || value === "") {
        return [];
    }
    if (Array.isArray(value)) {
        return value.flatMap(parseCompatibleAircraft);
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) {
            return [];
        }
        if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) {
                    return parseCompatibleAircraft(parsed);
                }
            } catch {
                // Fall through to CSV parsing for malformed legacy values.
            }
        }

        return trimmed.split(/[,;\n]/).map((item) => item.trim()).filter(Boolean);
    }
    if (typeof value === "object") {
        return [
            value.registration,
            value.aircraft_reg,
            value.aircraft_type,
            value.aircraft_type_id,
            value.type,
            value.code,
            value.value,
        ].filter((item) => item !== undefined && item !== null && item !== "");
    }

    return [value];
}

export function routeMatchesAircraft(route, aircraftReg) {
    const normalizedAircraftReg = normalizeAircraftIdentifier(aircraftReg);
    if (!normalizedAircraftReg || !Array.isArray(route?.compatible_aircraft) || route.compatible_aircraft.length === 0) {
        return true;
    }

    return route.compatible_aircraft.some((registration) => normalizeAircraftIdentifier(registration) === normalizedAircraftReg);
}

export function buildRouteReferenceData(routeOptions = []) {
    const routesById = {};
    const routes = [];

    routeOptions.forEach((option) => {
        const route = option?.route || option;
        // Compatibility needs full aircraft context; keep active routes for findMatchingRoute.
        if (!route || route.id == null || route.is_active === false) {
            return;
        }

        routesById[route.id] = route;
        routes.push(route);
    });

    return { routes, routesById };
}

export function mergeReferenceData(...referenceDataList) {
    const merged = {};
    const routesById = {};
    const routesByLeg = {};
    let hasRoutesByLeg = false;
    const collectedRoutes = [];
    const routes = [];
    const seenRouteKeys = new Set();

    referenceDataList.filter(Boolean).forEach((referenceData) => {
        Object.assign(merged, referenceData);
        Object.entries(routesByIdToObject(referenceData.routesById)).forEach(([routeId, route]) => {
            if (route) {
                routesById[routeId] = route;
            }
        });
        Object.entries(routesByLegToObject(referenceData.routesByLeg)).forEach(([legKey, legRoutes]) => {
            if (legRoutes) {
                routesByLeg[legKey] = legRoutes;
                hasRoutesByLeg = true;
            }
        });
        if (Array.isArray(referenceData.routes)) {
            collectedRoutes.push(...referenceData.routes.filter(Boolean));
        }
    });

    [...Object.values(routesById), ...collectedRoutes].forEach((route) => {
        const key = route?.id == null ? null : String(route.id);
        if (!key || seenRouteKeys.has(key)) {
            return;
        }
        seenRouteKeys.add(key);
        routes.push(route);
    });

    return {
        ...merged,
        routes,
        routesById,
        ...(hasRoutesByLeg ? { routesByLeg } : {}),
    };
}

function getMapOrObjectValue(collection, key) {
    if (!collection || key === undefined || key === null || key === "") {
        return null;
    }

    if (collection instanceof Map) {
        return collection.get(key) || collection.get(String(key)) || collection.get(normalizeAircraftIdentifier(key)) || null;
    }

    if (typeof collection === "object" && !Array.isArray(collection)) {
        return collection[key] || collection[String(key)] || collection[normalizeAircraftIdentifier(key)] || null;
    }

    return null;
}

function getAircraftByRegistration(referenceData = {}, aircraftReg) {
    const normalizedReg = normalizeAircraftIdentifier(aircraftReg);
    if (!normalizedReg) {
        return null;
    }

    const directCollections = [
        referenceData.aircraftsByRegistration,
        referenceData.aircraftByRegistration,
        referenceData.aircraftMap,
    ];
    for (const collection of directCollections) {
        const aircraft = getMapOrObjectValue(collection, aircraftReg)
            || getMapOrObjectValue(collection, normalizedReg)
            || valuesFromMapOrObject(collection).find((item) => (
                normalizeAircraftIdentifier(item?.registration || item?.aircraft_reg || item?.value) === normalizedReg
            ));
        if (aircraft) {
            return aircraft;
        }
    }

    const aircraftCollections = [
        referenceData.aircrafts,
        referenceData.aircraftList,
        referenceData.aircraftOptions,
    ];
    for (const collection of aircraftCollections) {
        const aircraft = valuesFromMapOrObject(collection).find((item) => (
            normalizeAircraftIdentifier(item?.registration || item?.aircraft_reg || item?.value) === normalizedReg
        ));
        if (aircraft) {
            return aircraft;
        }
    }

    return null;
}

function getAircraftTypeById(referenceData = {}, aircraftTypeId) {
    const directCollections = [
        referenceData.aircraftTypesById,
        referenceData.aircraftTypeById,
    ];
    for (const collection of directCollections) {
        const aircraftType = getMapOrObjectValue(collection, aircraftTypeId);
        if (aircraftType) {
            return aircraftType;
        }
    }

    const typeCollections = [
        referenceData.aircraftTypes,
        referenceData.aircraftTypeList,
    ];
    return typeCollections
        .flatMap(valuesFromMapOrObject)
        .find((item) => String(item?.id ?? item?.value ?? "") === String(aircraftTypeId ?? "")) || null;
}

function addAircraftIdentifiers(identifiers, values) {
    values.forEach((value) => {
        const normalized = normalizeAircraftIdentifier(value);
        if (normalized) {
            identifiers.add(normalized);
        }
    });
}

function getCurrentAircraftIdentifiers(currentFlight = {}, referenceData = {}) {
    const identifiers = new Set();
    const aircraft = currentFlight?.aircraft || currentFlight?.aircraft_snapshot || currentFlight?.aircraftSnapshot || {};
    const aircraftByReg = getAircraftByRegistration(referenceData, currentFlight?.aircraft_reg);
    const aircraftType = getAircraftTypeById(referenceData, currentFlight?.aircraft_type_id);

    addAircraftIdentifiers(identifiers, [
        currentFlight?.aircraft_reg,
        currentFlight?.aircraft_type,
        currentFlight?.aircraft_type_id,
        currentFlight?.aircraft_type_code,
        currentFlight?.aircraftType,
        currentFlight?.aircraftTypeId,
        aircraft?.registration,
        aircraft?.aircraft_reg,
        aircraft?.aircraft_type,
        aircraft?.aircraft_type_id,
        aircraftByReg?.aircraft_type,
        aircraftByReg?.aircraft_type_id,
        aircraftByReg?.type,
        aircraftType?.aircraft_type,
        aircraftType?.type,
        aircraftType?.code,
        aircraftType?.name,
        aircraftType?.value,
    ]);

    return identifiers;
}

function getAircraftCompatibilityScore(route, currentFlight = {}, referenceData = {}) {
    const compatibleAircraft = parseCompatibleAircraft(route?.compatible_aircraft).map(normalizeAircraftIdentifier).filter(Boolean);
    if (!compatibleAircraft.length) {
        return 1;
    }

    const currentAircraftIdentifiers = getCurrentAircraftIdentifiers(currentFlight, referenceData);
    if (!currentAircraftIdentifiers.size) {
        return 1;
    }

    return compatibleAircraft.some((aircraft) => currentAircraftIdentifiers.has(aircraft)) ? 0 : 2;
}

export function routeMatchesCurrentAircraft(route, currentFlight = {}, referenceData = {}) {
    return getAircraftCompatibilityScore(route, currentFlight, referenceData) < 2;
}

function compareRouteNames(left, right) {
    const nameCompare = String(left?.route_name || "").localeCompare(String(right?.route_name || ""));
    if (nameCompare !== 0) {
        return nameCompare;
    }
    return Number(left?.id || 0) - Number(right?.id || 0);
}

function resolveFindMatchingRouteArgs(depOrFlight, arrOrReferenceData, referenceDataArg, currentFlightArg) {
    if (typeof depOrFlight === "object" && !Array.isArray(depOrFlight)) {
        const currentFlight = depOrFlight || {};
        return {
            departureAirport: normalizeAirportCode(currentFlight?.departure_airport),
            arrivalAirport: normalizeAirportCode(currentFlight?.arrival_airport),
            referenceData: arrOrReferenceData || {},
            currentFlight,
        };
    }

    return {
        departureAirport: normalizeAirportCode(depOrFlight),
        arrivalAirport: normalizeAirportCode(arrOrReferenceData),
        referenceData: referenceDataArg || {},
        currentFlight: currentFlightArg || {},
    };
}

/**
 * 查找指定航段的最佳航路。兼容旧签名 findMatchingRoute(currentFlight, referenceData)。
 *
 * 顺序与后端 best-match、旧版加航段同一条：默认且这架飞机能用 → 适配这架飞机 → 这架飞机能用的。
 * 同一优先级里按航路名称升序取第一条；一条这架飞机能用的都没有时返回 null，
 * 不退回绑定「只适配别的飞机」的默认航路，也不直接拿列表第一条。
 *
 * 注意「能不能用」的判定沿用新版草稿既有口径（getAircraftCompatibilityScore：注册号，另兼容机型标识；
 * 航班飞机信息完全取不到时按不限飞机处理），与后端 / 旧版「只看注册号名单」在两类非规范数据上会不同：
 * 名单里写的是机型、或航班完全没有飞机信息。航路主数据页面只能选注册号，正常数据两版结果一致；
 * 这两类差异要收敛，需连同新版航路合法性校验（按机型/注册号判兼容）一起决定，不在本次范围。
 */
export function findMatchingRoute(depOrFlight, arrOrReferenceData, referenceDataArg, currentFlightArg) {
    const { departureAirport, arrivalAirport, referenceData, currentFlight } = resolveFindMatchingRouteArgs(
        depOrFlight,
        arrOrReferenceData,
        referenceDataArg,
        currentFlightArg,
    );
    if (!departureAirport || !arrivalAirport) {
        return null;
    }

    const matchingRoutes = getReferenceRoutes(referenceData, departureAirport, arrivalAirport).filter((route) => routeMatchesLeg(route, departureAirport, arrivalAirport));
    const usableRoutes = matchingRoutes
        .filter((route) => route?.is_active !== false && routeMatchesCurrentAircraft(route, currentFlight, referenceData))
        .sort(compareRouteNames);

    const defaultRoute = usableRoutes.find((route) => route?.is_default);
    if (defaultRoute) {
        return defaultRoute;
    }

    const specificRoute = usableRoutes.find((route) => getAircraftCompatibilityScore(route, currentFlight, referenceData) === 0);
    if (specificRoute) {
        return specificRoute;
    }

    return usableRoutes[0] || null;
}
